/**
 * hive hook <type> — Claude Code hook handler (plumbing, not user-facing).
 *
 * Reads Claude Code hook JSON from stdin, performs auto-mode operations,
 * and exits 0 always. Never prints errors to stderr. Total budget < 4 s (well
 * under Claude Code's 10s per-hook timeout, set in the .claude/settings.json
 * entries `aethereum init` writes).
 *
 * Hook types:
 *   session-start   — fetch team context and inject it as session context
 *   prompt          — share intent, heartbeat, and inject collision alerts
 *   stop            — heartbeat, block on unresolved alerts
 *   subagent-start  — same as session-start, scoped to a Task-tool subagent
 *   subagent-stop   — heartbeat + surface (never block) unresolved alerts
 *
 * Claude Code fires SubagentStart/SubagentStop for Task-tool subagents, NOT
 * SessionStart/Stop — a project that only wires the latter never hears from its
 * own fanned-out subagents. subagent-start/subagent-stop close that gap.
 */

import { loadAutoConfig, deriveIntent, shareIntentViaMcp, fetchContext, heartbeat } from "../hive/auto.js";
import { formatContractChangedPush } from "../hive/mcp.js";
import { captureHookSessionEvent, updateRollups } from "../sessions/index-store.js";
import { publishUsage } from "../sessions/publisher.js";
import { resolveProject } from "../hive/config-store.js";

type HookType = "session-start" | "prompt" | "stop" | "post-tool-use" | "subagent-start" | "subagent-stop";

/**
 * Read all of stdin into a string. Returns "" if stdin is empty or not a TTY.
 */
async function readStdin(): Promise<string> {
  if (process.stdin.isTTY) return "";
  return new Promise<string>((resolve) => {
    const chunks: Buffer[] = [];
    process.stdin.on("data", (chunk: Buffer) => chunks.push(chunk));
    process.stdin.on("end", () => resolve(Buffer.concat(chunks).toString("utf8")));
    process.stdin.on("error", () => resolve(""));
  });
}

function safeParseJson(raw: string): Record<string, unknown> {
  if (!raw.trim()) return {};
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Format the collision alerts on a context payload into pushable lines (or ""). */
function formatAlerts(ctx: Awaited<ReturnType<typeof fetchContext>>): string {
  if (!ctx || ctx.alerts.length === 0) return "";
  return ctx.alerts
    .map((a) =>
      formatContractChangedPush({
        type: "contract_changed",
        name: a.name,
        from: a.from,
        to: a.to,
        oldShape: a.oldShape,
        newShape: a.newShape,
        ts: a.ts,
      }),
    )
    .join("\n")
    .trim();
}

export async function runHook(hookType: HookType, args: string[], rawStdin?: string): Promise<void> {
  // Never exit non-zero; wrap everything
  try {
    await _runHook(hookType, args, rawStdin);
  } catch {
    // swallow all errors
  }
}

/**
 * v4-S9 session capture: append ONE index line (sync, bounded, fail-soft) for
 * session-start/stop bodies that carry a session_id, and on stop kick the
 * INCREMENTAL rollup update with a hard time bound. If the rollup cannot
 * finish quickly it checkpoints and returns; a later `aethereum sessions` /
 * `usage` run catches up. This must NEVER block or fail the hook.
 */
async function captureSession(hookType: HookType, body: Record<string, unknown>): Promise<void> {
  if (hookType !== "session-start" && hookType !== "stop") return;
  try {
    const cwd = typeof body["cwd"] === "string" ? (body["cwd"] as string) : process.cwd();
    let roomId: string | undefined;
    try {
      roomId = resolveProject(cwd)?.roomId;
    } catch {
      roomId = undefined;
    }
    const line = captureHookSessionEvent(hookType === "session-start" ? "start" : "stop", body, { roomId });
    if (hookType === "stop" && line?.transcriptPath) {
      // One shared deadline for rollup + publish: the publisher may only spend
      // whatever the rollup left over, NEVER extending the capture budget.
      const ROLLUP_DEADLINE_MS = 1_500;
      const captureDeadline = Date.now() + ROLLUP_DEADLINE_MS + 250;
      await Promise.race([
        updateRollups({ onlyTranscript: line.transcriptPath, budgetMs: ROLLUP_DEADLINE_MS }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), ROLLUP_DEADLINE_MS + 250)),
      ]);
      // v4-S9 usage-rollup publish: silent no-op without local consent
      // (`usageRollups: true`) + a token; throttled internally; skipped
      // entirely when the rollup already ate the budget.
      const remainingMs = captureDeadline - Date.now();
      if (remainingMs > 100) {
        await Promise.race([
          publishUsage({ cwd }),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), remainingMs)),
        ]);
      }
    }
  } catch {
    // session capture is best-effort, never a hook failure
  }
}

/** True when this process was launched via the npx cache (cold-start pays the
 *  npx resolve/fetch cost BEFORE node runs, so network budgets can start late). */
function runningUnderNpx(argv1: string = process.argv[1] ?? ""): boolean {
  return /[\\/]_npx[\\/]/.test(argv1) || /[\\/]\.npm[\\/]_npx[\\/]/.test(argv1);
}

async function _runHook(hookType: HookType, _args: string[], rawStdin?: string): Promise<void> {
  const raw = rawStdin ?? (await readStdin());
  const body = safeParseJson(raw);

  // Session capture is local-only and runs even when no room is configured.
  // For the stop hook it runs CONCURRENTLY with the alert fetch below (they are
  // independent: capture is local disk + rollup, the fetch is network), so the
  // hook's wall time is max(capture, fetch) instead of their sum.
  const capturePromise = captureSession(hookType, body);
  if (hookType !== "stop") await capturePromise;

  const cfg = loadAutoConfig(process.cwd(), process.env as Record<string, string | undefined>);
  if (!cfg) {
    if (hookType === "stop") await capturePromise; // finish the local capture
    return; // not configured — silent
  }

  if (hookType === "session-start") {
    // Fetch team context (NON-consuming: peek) and inject as session context.
    // The hooks must not eat the alert cursor — only the agent's explicit
    // get_team_context / await_team_events consumes (bug B3). One-shot, so we
    // allow a slightly larger deadline (4s), still well under the 10s budget.
    const SESSION_DEADLINE_MS = 4_000;
    const ctx = await Promise.race([
      fetchContext(cfg.url, cfg.token, { peek: true }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), SESSION_DEADLINE_MS)),
    ]);
    if (ctx && ctx.text.trim()) {
      process.stdout.write(`Aethereum team context:\n${ctx.text}`);
    }
    return;
  }

  if (hookType === "subagent-start") {
    // Same fetch as session-start (peek, inject as context) but a DIFFERENT output
    // shape: SessionStart auto-injects plain stdout text, but SubagentStart does
    // NOT — it only honors the structured hookSpecificOutput.additionalContext
    // JSON form (same contract as PostToolUse/SubagentStop). Plain stdout here
    // is silently dropped and never reaches the subagent.
    // Must clear fetchContext's own 3000ms inner AbortSignal with margin, or this
    // outer race always wins first and the inner timeout value is moot.
    const SUBAGENT_START_DEADLINE_MS = 3_500;
    const ctx = await Promise.race([
      fetchContext(cfg.url, cfg.token, { peek: true }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), SUBAGENT_START_DEADLINE_MS)),
    ]);
    if (ctx && ctx.text.trim()) {
      process.stdout.write(
        JSON.stringify({
          hookSpecificOutput: {
            hookEventName: "SubagentStart",
            additionalContext: `Aethereum team context:\n${ctx.text}`,
          },
        }),
      );
    }
    return;
  }

  if (hookType === "prompt") {
    const prompt = typeof body["prompt"] === "string" ? body["prompt"] : "";
    const intent = deriveIntent(prompt);

    // Intent share + heartbeat are NOT needed before the prompt runs — kick
    // them off and move on (true fire-and-forget, never awaited).
    if (intent) {
      void shareIntentViaMcp(cfg.url, cfg.token, intent);
      void heartbeat(cfg.url, cfg.token, intent);
    }

    // Only the alert fetch gates the prompt output, and only briefly.
    // Must clear fetchContext's own 3000ms inner AbortSignal with margin.
    // The hook ALWAYS returns well under the 10s budget.
    const ALERT_DEADLINE_MS = 3_500;
    const ctx = await Promise.race([
      fetchContext(cfg.url, cfg.token, { peek: true }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), ALERT_DEADLINE_MS)),
    ]);

    // Print only alert lines — inject right before prompt runs
    if (ctx && ctx.alerts.length > 0) {
      const lines = ctx.alerts
        .map((a) =>
          formatContractChangedPush({
            type: "contract_changed",
            name: a.name,
            from: a.from,
            to: a.to,
            oldShape: a.oldShape,
            newShape: a.newShape,
            ts: a.ts,
          }),
        )
        .join("\n");
      if (lines.trim()) {
        process.stdout.write(lines + "\n");
      }
    }
    return;
  }

  if (hookType === "post-tool-use") {
    // The gentle, always-on live layer: between EVERY tool call, peek fresh alerts
    // (NON-consuming) and inject them as additionalContext, so the agent hears about
    // a teammate's collision without having to ask. Non-consuming so the explicit
    // get_team_context still delivers exactly-once (bug B3); the same alert may
    // re-surface here until the agent reads it, which is the intended nudge. Non-
    // interrupting — it sees + keeps doing its current task. Short, hard deadline —
    // deliberately shorter than fetchContext's own 3000ms timeout, so this fires on
    // every single tool call without adding real latency; it will frequently lose
    // the race and no-op, which is fine since it is a supplementary nudge, not the
    // only delivery path (prompt/stop/subagent-stop give the fetch a fair shot).
    // Under npx the process paid a cold-start before this code even ran; the
    // remaining fetch window can be all the hook has, so give it a wider budget
    // there (still far under the 10s hook timeout).
    const POST_DEADLINE_MS = runningUnderNpx() ? 2_800 : 1_500;
    const ctx = await Promise.race([
      fetchContext(cfg.url, cfg.token, { peek: true }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), POST_DEADLINE_MS)),
    ]);
    const lines = formatAlerts(ctx);
    if (lines) {
      process.stdout.write(
        JSON.stringify({
          hookSpecificOutput: { hookEventName: "PostToolUse", additionalContext: lines },
        }),
      );
    }
    return;
  }

  if (hookType === "stop") {
    // Heartbeat is fire-and-forget.
    void heartbeat(cfg.url, cfg.token);
    // Aggressive-but-graceful (default on; AETHEREUM_LIVE_STOP=0 disables): if there
    // are still-unaddressed alerts at the turn boundary, keep the agent going to
    // handle a teammate's change before it idles. It finishes its task first (the
    // PostToolUse layer never interrupts mid-task), then deals with the alert here.
    // NON-consuming (bug B3): the block persists until the agent's explicit
    // get_team_context consumes the alert; Claude Code caps stop-blocks per turn so
    // this can't loop within a turn.
    if (process.env.AETHEREUM_LIVE_STOP === "0") {
      await capturePromise;
      return;
    }
    // Must clear fetchContext's own 3000ms inner AbortSignal with margin.
    // Runs CONCURRENTLY with the session capture/rollup kicked off above (one
    // shared wall-clock window instead of capture-then-fetch serially).
    const STOP_DEADLINE_MS = 3_500;
    const [, ctx] = await Promise.all([
      capturePromise,
      Promise.race([
        fetchContext(cfg.url, cfg.token, { peek: true }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), STOP_DEADLINE_MS)),
      ]),
    ]);
    const lines = formatAlerts(ctx);
    if (lines) {
      process.stdout.write(
        JSON.stringify({
          decision: "block",
          reason: `${lines}\n\nA teammate's change affects work you depend on. Handle it before finishing.`,
        }),
      );
    }
    return;
  }

  if (hookType === "subagent-stop") {
    // Heartbeat only, same as stop — but NEVER block. A subagent is finishing a
    // narrow delegated task chosen by its parent; it usually has neither the
    // context nor the tools to go fix an unrelated teammate's breaking change, so
    // forcing it to keep running would just waste its turn. Surface the alert as
    // non-blocking additionalContext instead, same delivery shape as post-tool-use,
    // so it still reaches the parent's transcript without stalling the subagent.
    void heartbeat(cfg.url, cfg.token);
    // Must clear fetchContext's own 3000ms inner AbortSignal with margin.
    const SUBAGENT_STOP_DEADLINE_MS = 3_500;
    const ctx = await Promise.race([
      fetchContext(cfg.url, cfg.token, { peek: true }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), SUBAGENT_STOP_DEADLINE_MS)),
    ]);
    const lines = formatAlerts(ctx);
    if (lines) {
      process.stdout.write(
        JSON.stringify({
          hookSpecificOutput: { hookEventName: "SubagentStop", additionalContext: lines },
        }),
      );
    }
    return;
  }
}
